import React, { useLayoutEffect, useEffect, useRef, useState, useContext } from 'react';
import clsx from 'clsx';
import SidebarLinkGroup from 'components/SidebarLinkGroup';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as RingsIcon } from 'assets/img/icons/rings.svg';

import { useDispatch, useSelector } from 'react-redux';
import { handleSidebarAbsolute, handleSidebarOpen, handleSidebarWidth } from 'store/appSlice';
import { RootState } from 'store';
import { HomeIcon } from 'components/icons/HomeIcon';
import { Menu } from 'components/Menu';
import { v4 as uuid } from 'uuid';
import { PlusIcon } from 'components/icons/PlusIcon';
import { FolderIcon } from 'components/icons/FolderIcon';
import { UsersIcon } from 'components/icons/UsersIcon';
import { GridIcon } from 'components/icons/GridIcon';
import { ChatIcon } from 'components/icons/ChatIcon';
import { PlayAltIcon } from 'components/icons/PlayAltIcon';
import VideospaceIcon from 'assets/img/icons/videospace_icon.png';
import { HashIcon } from 'components/icons/HashIcon';
import { LockIcon } from 'components/icons/LockIcon';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { MenuItem } from 'components/Menu/MenuItem';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { VideosIcon } from 'components/icons/VideosIcon';
import { isMobileOnly } from 'react-device-detect';
import useActiveWorkspace from 'hooks/useActiveWorkspace';
import { removeWorkspace } from 'store/workspaceSlice';

const scrollContainer = {
  maxHeight: isMobileOnly ? `${window.innerHeight - 100}px` : 'calc(100vh - 100px)'
};

const menus = [
  {
    id: uuid(),
    icon: <ChatIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
    name: 'Recent notes and comments',
    devider: true
  },
  {
    id: uuid(),
    icon: <PlayAltIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
    name: 'Browse all channels'
  },
  {
    id: uuid(),
    icon: <FolderIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
    name: 'Manage my videos'
  },
  {
    id: uuid(),
    icon: <UsersIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
    name: 'People & user groups'
  },
  {
    id: uuid(),
    icon: <GridIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
    name: 'Apps'
  }
];

function StudioSidebar() {
  const location = useLocation();
  const dispatch = useDispatch();
  const app = useSelector((state: RootState) => state.app);
  const { sidebarOpen, secondaryViewWidth, secondaryViewOpen, sidebarWidth, sidebarAbsolute } = app;
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  // to be removed migrate to new activity slice
  // const newlyAddedVideos = useSelector((state: RootState) => state.video.newlyAddedVideos);
  // const newlyAddedActivities = useSelector((state: RootState) => state.activity.newlyAddedActivities);

  const channelActivities = useSelector((state: RootState) => state.userChannelActivity);

  const { pathname } = location;
  const isVideoPage = pathname?.match('/videos/') ? true : false;
  const sidebar = useRef(document.createElement('div'));
  const { showModal } = useContext(ModalContext);

  const [isResizing, setIsResizing] = useState(false);
  const [manualResize, setManualResize] = useState(false);
  const [displayResizer] = useState(true);
  // const [displayResizer, handleDisplayResizer] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const { workspaceId, activeWorkspace } = useActiveWorkspace();
  const [userData, setUserData] = useState('') as any;

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const createChannel = () => {
    showModal(MODAL_TYPES.create_channel, {
      animation: MODAL_ANIMATIONS.fade
    });
  };

  const channelMenus = [
    {
      id: uuid(),
      name: 'Browse channels'
    },
    {
      id: uuid(),
      name: 'Create a channel',
      onClick: createChannel
    }
  ];

  const workspaceMenus = [
    {
      id: uuid(),
      name: `<div class="leading-[18px] py-2"><span class="font-black">${
        activeWorkspace?.name ? activeWorkspace?.name : 'Nanotech'
      }</span> <p>${
        activeWorkspace?.url ? activeWorkspace.url.replace('https://', '') : 'nanotech.videospace.ai'
      }</p></div>`,
      icon: <img src={VideospaceIcon} className="w-9 h-9 rounded-md mr-3" alt="videospace" />,
      devider: true
    },
    {
      id: uuid(),
      name: "<div class='text-sm'>Your workspace is currently on Videospace's <span class='font-black'>Enterprise plan</span>.</div>",
      devider: true
    },
    {
      id: uuid(),
      name: 'Zoom Webinar and Events',
      onClick: () => {
        window.location.href = `${activeWorkspace?.url}/user/home/`;
      }
    },
    {
      id: uuid(),
      name: `Invite people to ${activeWorkspace?.name}`,
      onClick: () => {
        window.location.href = `${activeWorkspace?.url}/settings/member-invites/`;
      }
    },
    {
      id: uuid(),
      name: 'Create a channel',
      onClick: createChannel,
      devider: true
    },
    // {
    //   id: uuid(),
    //   name: 'Preferences'
    // },
    {
      id: uuid(),
      name: 'Settings (all items)',
      subMenus: [
        {
          id: uuid(),
          pointer: 'disabled',
          name: '<span class="text-[13px] font-normal text-tabBorder">settings</span>'
        },
        {
          id: uuid(),
          name: 'Workspace settings'
        },
        {
          id: uuid(),
          name: 'Customize Nanotech'
        },
        {
          id: uuid(),
          name: 'Edit workspace details',
          devider: true
        },
        {
          id: uuid(),
          pointer: 'disabled',
          name: '<span class="text-[13px] font-normal text-tabBorder">administration</span>'
        },
        {
          id: uuid(),
          name: 'Manage members',
          onClick: () => {
            window.location.href = `${activeWorkspace.url}/settings/members/`;
          }
        }
        // {
        //   id: uuid(),
        //   name: 'Manage apps'
        // },
        // {
        //   id: uuid(),
        //   name: 'Billing'
        // }
      ]
    },
    {
      id: uuid(),
      name: `Sign out of ${activeWorkspace?.name}`,
      onClick: () => {
        const logoutUrl = activeWorkspace?.url;
        dispatch(removeWorkspace(activeWorkspace));
        window.location.replace(logoutUrl + '/logout');
      }
    }
  ];

  function updateSidebarStates() {
    const sidebarDom = document.getElementById('sidebar');
    if (sidebarDom) {
      const sidebarWidth = sidebarDom.clientWidth;
      if (!secondaryViewOpen && !isVideoPage) {
        if (window.innerWidth - sidebarWidth < 963) {
          // handleDisplayResizer(false);
          dispatch(handleSidebarOpen(false));
          dispatch(handleSidebarAbsolute(true));
        } else {
          // handleDisplayResizer(true);
          dispatch(handleSidebarOpen(true));
          dispatch(handleSidebarAbsolute(false));
        }
      }
    }
  }

  useEffect(() => {
    if (secondaryViewOpen) {
      if (window.innerWidth - secondaryViewWidth - sidebarWidth < 500) {
        dispatch(handleSidebarAbsolute(true));
      } else {
        if (sidebarOpen) {
          dispatch(handleSidebarAbsolute(false));
        } else {
          dispatch(handleSidebarAbsolute(true));
        }
      }
    } else {
      if (window.innerWidth - sidebarWidth < 385) {
        dispatch(handleSidebarAbsolute(true));
      } else {
        if (sidebarOpen) {
          dispatch(handleSidebarAbsolute(false));
        } else {
          dispatch(handleSidebarAbsolute(true));
        }
      }
    }
  }, [sidebarOpen]);

  useLayoutEffect(() => {
    if (!isVideoPage) {
      window.addEventListener('resize', updateSidebarStates);
    }
    updateSidebarStates();
    return () => window.removeEventListener('resize', updateSidebarStates);
  }, [isVideoPage]);

  const startResizing = React.useCallback(() => {
    setManualResize(true);
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback(
    (mouseMoveEvent) => {
      if (isResizing && !isVideoPage) {
        if (manualResize) {
          if (secondaryViewOpen) {
            if (mouseMoveEvent.clientX < 140) {
              dispatch(handleSidebarOpen(false));
              dispatch(handleSidebarWidth(180));
            } else {
              dispatch(handleSidebarOpen(true));
              if (window.innerWidth - mouseMoveEvent.clientX - secondaryViewWidth > 500) {
                dispatch(handleSidebarWidth(mouseMoveEvent.clientX));
              } else {
                dispatch(handleSidebarWidth(window.innerWidth - 500 - secondaryViewWidth));
              }
            }
          } else {
            if (mouseMoveEvent.clientX < 140) {
              dispatch(handleSidebarOpen(false));
              dispatch(handleSidebarWidth(180));
            } else if (mouseMoveEvent.clientX > 600) {
              dispatch(handleSidebarWidth(600));
            } else {
              dispatch(handleSidebarOpen(true));
              dispatch(handleSidebarWidth(mouseMoveEvent.clientX));
            }
          }
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (!isVideoPage) {
      if (secondaryViewOpen) {
        if (window.innerWidth - secondaryViewWidth - sidebarWidth < 500) {
          dispatch(handleSidebarOpen(false));
          dispatch(handleSidebarAbsolute(true));
          // setSidebarWidth(180);
        } else {
          dispatch(handleSidebarOpen(true));
          dispatch(handleSidebarAbsolute(false));
        }
      } else {
        if (window.innerWidth - sidebarWidth < 385) {
          dispatch(handleSidebarAbsolute(true));
        } else {
          if (sidebarOpen) {
            dispatch(handleSidebarAbsolute(false));
          } else {
            dispatch(handleSidebarAbsolute(true));
          }
        }
      }
    }
  }, [secondaryViewWidth]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: any) => {
      if (!sidebarOpen || keyCode !== 27) return;
      dispatch(handleSidebarOpen(false));
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  const handleDblClick = (e: any) => {
    if (e.detail === 2) {
      dispatch(handleSidebarWidth(200));
      setManualResize(false);
    }
  };

  useEffect(() => {
    if (isMobileOnly) {
      dispatch(handleSidebarOpen(false));
    }
  }, []);

  return (
    <div className="font-lato z-20">
      <div
        className={`fixed inset-0 bg-transparent z-40 lg:z-auto transition-opacity  ${
          sidebarOpen && sidebarAbsolute ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
        onClick={() => dispatch(handleSidebarOpen(false))}
      ></div>
      <div
        id="sidebar"
        ref={sidebar}
        style={{
          width: manualResize ? sidebarWidth : 'none',
          transform: sidebarOpen ? 'translate(0px)' : `translate(-${sidebarWidth + 60}px)`,
          position: sidebarAbsolute ? 'absolute' : 'static'
        }}
        className={clsx(
          'border-t border-gray-border app-sidebar flex flex-col z-40 left-0 top-11 md:left-auto md:top-auto transform h-[calc(100vh-44px)] shrink-0 bg-gray-sidebar transition-all duration-0 ease-in-out overflow-visible 2xl:flex-1 ',
          {
            'min-w-[180px] md:min-w-[180px] lg:min-w-[180px] xl:min-w-[180px]': manualResize,
            'min-w-[200px] md:min-w-[200px] lg:min-w-[220px] xl:min-w-[260px]': !manualResize
          }
        )}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="app-sidebar-content">
          <div>
            <Menu
              menus={workspaceMenus}
              align="left"
              MenuItem={MenuItem}
              width={304}
              position="fixed"
              additionalClasses="w-full border-b border-gray-border "
            >
              <div className="flex px-4 font-black text-white leading-6 h-[50px] items-center cursor-pointer hover:bg-white hover:bg-opacity-10">
                <span className="text-[17px]">{activeWorkspace?.name ? activeWorkspace?.name : 'Nanotech'}</span>
                <FontAwesomeIcon icon={faAngleDown} className="w-2 p-2" />
              </div>
            </Menu>
            <PerfectScrollbar component="div" style={scrollContainer} options={{ suppressScrollY: false }}>
              <div>
                <ul className="pt-3">
                  {/* <li
                    className={`px-4 rounded-sm last:mb-0 ${
                      pathname === '/home' ? 'bg-blue-active' : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to="/home"
                      className={`block text-white font-normal text-opacity-70 transition ${
                        pathname === '/home' && 'font-bold text-opacity-100'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <HomeIcon className="w-[18px] h-[18px]" fill="currentColor" />
                        </span>
                        <span className="text-[15px] leading-[28px] truncate">Home</span>
                      </div>
                    </NavLink>
                  </li> */}
                  <li
                    className={`px-4 rounded-sm last:mb-0 ${
                      pathname === '/videos/all' ? 'bg-blue-active' : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`/workspace/${workspaceId}/videos/all`}
                      className={`block text-white font-normal transition text-[15px] leading-[28px]  ${
                        pathname === `/workspace/${workspaceId}/videos/all` && 'font-medium text-opacity-100'
                      }
                    
                      ${
                        channelActivities['home']?.newActivityIds.length
                          ? 'text-base font-semibold text-white'
                          : 'text-opacity-70 '
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <VideosIcon className="w-[18px] h-[18px]" stroke="currentColor" />
                        </span>
                        <span className="truncate">My Library</span>
                      </div>
                    </NavLink>
                  </li>
                  {/* <li
                    className={`px-4 rounded-sm last:mb-0 ${
                      pathname.includes('live') ? 'bg-blue-active' : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to="/live"
                      className={`block text-white text-opacity-70 ${pathname.includes('live') && 'text-opacity-100'}`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <CommentIcon className="w-[18px] h-[18px]" stroke="currentColor" />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Messages and Notes</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 rounded-sm last:mb-0 ${
                      pathname.includes('saved') ? 'bg-blue-active' : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to="/saved"
                      className={`block text-white text-opacity-70 ${pathname.includes('saved') && 'text-opacity-100'}`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <SearchIcon className="w-[18px] h-[18px]" />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Browse all channels</span>
                      </div>
                    </NavLink>
                  </li>
                  <Menu menus={menus} align="left" MenuItem={MenuItem} position="fixed" width={265}>
                    <li className="px-4 rounded-sm last:mb-0 cursor-pointer">
                      <div className="block text-white text-opacity-70">
                        <div className="flex items-center">
                          <span className="w-[26px]">
                            <PeopleIcon className="w-[18px] h-[18px]" />
                          </span>
                          <span className="text-[15px] font-normal leading-[28px] truncate">People</span>
                        </div>
                      </div>
                    </li>
                  </Menu> */}
                  <div className="my-2 border-b border-gray-border"></div>
                  <SidebarLinkGroup>
                    {(handleClick, open) => {
                      return (
                        <React.Fragment>
                          <div
                            className={`px-4 block text-white text-opacity-70 -ml-1`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleClick();
                            }}
                          >
                            <div
                              className="flex items-center justify-between w-full"
                              onMouseEnter={handleMouseOver}
                              onMouseLeave={handleMouseOut}
                            >
                              <div className="flex items-center w-full">
                                <span className="w-[26px] h-[26px] hover:bg-white hover:bg-opacity-10 hover:rounded-md flex items-center justify-center cursor-pointer">
                                  {open ? (
                                    <FontAwesomeIcon icon={faCaretDown} className="w-[14px] h-[14px]" />
                                  ) : (
                                    <FontAwesomeIcon icon={faCaretRight} className="w-[14px] h-[14px]" />
                                  )}
                                </span>
                                <span className="ml-1 text-[15px] font-normal leading-[28px] truncate">Channels</span>
                                <div className="ml-auto flex items-center justify-center">
                                  <Menu menus={channelMenus} MenuItem={MenuItem} position="fixed" align="left">
                                    <PlusIcon
                                      fill="currentColor"
                                      className={`hover:bg-white hover:bg-opacity-10 rounded-[4px] cursor-pointer ${
                                        isHovering ? 'block' : 'hidden'
                                      }`}
                                    />
                                  </Menu>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <ul className={`${!open && 'hidden'}`}>
                              {channelList.map((channel: any) => (
                                <li
                                  className={`px-4 ${
                                    pathname.includes(channel.hash_id) ? 'bg-blue-active' : 'hover:bg-gray-highlight'
                                  }`}
                                  key={uuid()}
                                >
                                  <NavLink
                                    end
                                    to={`/workspace/${workspaceId}/user/channel/${channel.hash_id}`}
                                    className={`block truncate font-normal text-white text-[15px] leading-[28px] ${
                                      pathname.includes(channel.hash_id) && 'font-medium text-opacity-100'
                                    } ${
                                      channelActivities[channel.hash_id]?.newActivityIds.length
                                        ? 'text-base font-semibold text-white'
                                        : 'text-opacity-70 '
                                    }`}
                                  >
                                    <div className="flex items-center">
                                      <span className="w-[26px]">
                                        {channel.privacy === 'public' && (
                                          <HashIcon className="w-[18px] h-[18px]" fill="currentColor" />
                                        )}
                                        {channel.privacy === 'private' && (
                                          <LockIcon className="w-[18px] h-[14px]" fill="currentColor" />
                                        )}
                                      </span>
                                      <span className="truncate">{channel.name}</span>
                                      {channel.is_shared_channel && (
                                        <RingsIcon className="w-3 h-3 ml-auto" fill="currentColor" />
                                      )}
                                    </div>
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </React.Fragment>
                      );
                    }}
                  </SidebarLinkGroup>
                </ul>
              </div>
            </PerfectScrollbar>
          </div>
        </div>
        <div
          className={clsx('app-sidebar-resizer', {
            block: displayResizer,
            hidden: !displayResizer
          })}
          style={{ left: sidebarWidth - 4 }}
          onMouseDown={startResizing}
          onClick={handleDblClick}
        />
      </div>
    </div>
  );
}

export default StudioSidebar;
